<script setup lang="ts">
  import type { HTMLAttributes } from 'vue';

  import type { Email } from '@stuut/api/models';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import {
    PropertyList,
    PropertyListItem,
    PropertyListItemLabel,
    PropertyListItemValue,
  } from '@ui/components/property-list';
  import { cn } from '@ui/utils/utils';

  interface Props {
    class?: HTMLAttributes['class'];
    email: Email;
    triggerClass?: HTMLAttributes['class'];
  }

  const props = defineProps<Props>();

  const emailToHref = (email: string) => `mailto:${email}`;
</script>

<template>
  <Popover>
    <PopoverTrigger
      :class="cn('overflow-ellipsis overflow-hidden max-w-fit pr-1', triggerClass)"
      @click.stop
    >
      <slot name="trigger" />
    </PopoverTrigger>

    <PopoverContent :class="props.class">
      <PropertyList class="shrink-0 [&>.property-list-item]:grid-cols-[60px_1fr]!" size="sm">
        <PropertyListItem v-if="email.from_email">
          <PropertyListItemLabel> from:</PropertyListItemLabel>

          <PropertyListItemValue>
            <a :href="emailToHref(email.from_email)">
              {{ email.from_email }}
            </a>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem>
          <PropertyListItemLabel> to:</PropertyListItemLabel>

          <PropertyListItemValue>
            <p v-for="e in email.to" :key="e">
              <a :href="emailToHref(e)"> {{ e }} </a>
            </p>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="email.cc.length">
          <PropertyListItemLabel> cc:</PropertyListItemLabel>

          <PropertyListItemValue>
            <p v-for="e in email.cc" :key="e">
              <a :href="emailToHref(e)"> {{ e }} </a>
            </p>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="email.bcc.length">
          <PropertyListItemLabel> bcc:</PropertyListItemLabel>

          <PropertyListItemValue>
            <p v-for="e in email.bcc" :key="e">
              <a :href="emailToHref(e)"> {{ e }} </a>
            </p>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="email.sent_at">
          <PropertyListItemLabel> date:</PropertyListItemLabel>

          <PropertyListItemValue>
            {{ email.sent_at.local().format('L h:mm a') }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="email.sent_at">
          <PropertyListItemLabel> subject:</PropertyListItemLabel>

          <PropertyListItemValue>
            {{ email.subject }}
          </PropertyListItemValue>
        </PropertyListItem>
      </PropertyList>
    </PopoverContent>
  </Popover>
</template>

<style scoped lang="postcss">
  @reference "../../globals.css";

  a {
    @apply text-blue;
  }
</style>
